import { Injectable } from '@angular/core';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Message } from '@app/ngrx-message/models/client/message';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  baseMPPTransform,
  MPPTracingConstants,
  MPP_ACTIONS,
  RECIPE_ACTIONS,
  selectRecipeImagesLoaded,
} from '@usf/ngrx-tools';
import { firstValueFrom, tap } from 'rxjs';
import { RecipeImageService } from '../shared/services/recipe-image/recipe-image.service';
import { MppService } from '../service/mpp.service';

@Injectable({
  providedIn: 'root',
})
export class ClientMppEffects {
  archiveRecipeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.archiveRecipesSuccess),
        tap(action => {
          const spanPrefix = action?.archiveRecipe?.isArchive ? '' : 'Un';
          const eventPrefix = action?.archiveRecipe?.isArchive ? '' : 'un';
          this.store.dispatch(
            MPP_ACTIONS.archiveRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.archiveRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        eventPrefix +
                        MPPTracingConstants.archiveRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventSuccess,
                      spanName:
                        spanPrefix +
                        MPPTracingConstants.archiveRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanSuccess,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          this.displayToastMessage(
            MessageTypeEnum.success,
            this.translateService.instant(
              action?.archiveRecipe?.isArchive
                ? 'i18n.menuProfitPro.message.archiveSuccess'
                : 'i18n.menuProfitPro.message.unarchiveSuccess',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  archiveRecipeFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.archiveRecipesFailure),
        tap(action => {
          const spanPrefix = action?.archiveRecipe?.isArchive ? '' : 'Un';
          const eventPrefix = action?.archiveRecipe?.isArchive ? '' : 'un';
          this.store.dispatch(
            MPP_ACTIONS.archiveRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.archiveRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        eventPrefix +
                        MPPTracingConstants.archiveRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventFailure,
                      spanName:
                        spanPrefix +
                        MPPTracingConstants.archiveRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanFailure,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          this.displayToastMessage(
            MessageTypeEnum.error,
            this.translateService.instant(
              action?.archiveRecipe?.isArchive
                ? 'i18n.menuProfitPro.message.archiveFail'
                : 'i18n.menuProfitPro.message.unarchiveFail',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  createRecipeFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.createRecipeFailure),
        tap(action => {
          this.store.dispatch(
            MPP_ACTIONS.createRecipe({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.createRecipeEvent,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.createRecipeEvent +
                        ' ' +
                        MPPTracingConstants.eventFailure,
                      spanName:
                        MPPTracingConstants.createRecipeSpan +
                        ' ' +
                        MPPTracingConstants.spanFailure,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          this.displayToastMessage(
            MessageTypeEnum.error,
            this.translateService.instant(
              'i18n.menuProfitPro.message.createRecipeFail',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  createRecipeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.createRecipeSuccess),
        tap(action => {
          this.store.dispatch(
            MPP_ACTIONS.createRecipe({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.createRecipeEvent,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.createRecipeEvent +
                        ' ' +
                        MPPTracingConstants.eventSuccess,
                      spanName:
                        MPPTracingConstants.createRecipeSpan +
                        ' ' +
                        MPPTracingConstants.spanSuccess,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          this.mppService.goToDetails(action.recipe.recipeId);
        }),
      ),
    { dispatch: false },
  );

  copyRecipeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.copyRecipeSuccess),
        tap(action => {
          this.store.dispatch(
            MPP_ACTIONS.copyRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.copyRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.copyRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventSuccess,
                      spanName:
                        MPPTracingConstants.copyRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanSuccess,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          this.displayToastMessage(
            MessageTypeEnum.success,
            this.translateService.instant(
              'i18n.menuProfitPro.copyModal.toast.success',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  copyRecipeFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.copyRecipeFailure),
        tap(action => {
          this.store.dispatch(
            MPP_ACTIONS.copyRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.copyRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.copyRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventFailure,
                      spanName:
                        MPPTracingConstants.copyRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanFailure,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          this.displayToastMessage(
            MessageTypeEnum.error,
            this.translateService.instant(
              'i18n.menuProfitPro.copyModal.toast.error',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  uploadToS3Success$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.uploadRecipeProfileImageToS3Success),
        tap(({ uploadImageResponse }) => {
          this.store.dispatch(
            MPP_ACTIONS.uploadImage({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.uploadImage,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.uploadImageEvent +
                        ' ' +
                        MPPTracingConstants.eventSuccess,
                      spanName:
                        MPPTracingConstants.uploadImageSpan +
                        ' ' +
                        MPPTracingConstants.spanSuccess,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          const recipeId = uploadImageResponse.recipeId;
          this.store.dispatch(
            RECIPE_ACTIONS.getRecipeProfileImagesFromS3({
              getImagesRequest: [
                {
                  recipeId,
                  imageWaterMark: '', // explicitly set to null to fetch latest image
                },
              ],
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  uploadToS3Fail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.uploadRecipeProfileImageToS3Failure),
        tap(action => {
          this.store.dispatch(
            MPP_ACTIONS.uploadImage({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.uploadImage,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.uploadImageEvent +
                        ' ' +
                        MPPTracingConstants.eventFailure,
                      spanName:
                        MPPTracingConstants.uploadImageSpan +
                        ' ' +
                        MPPTracingConstants.spanFailure,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          this.displayToastMessage(
            MessageTypeEnum.error,
            this.translateService.instant(
              'i18n.menuProfitPro.uploadRecipeImageModal.toast.error',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  getRecieImages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.getRecipesSuccess),
        tap(async action => {
          this.store.dispatch(
            MPP_ACTIONS.getRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.getRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.getRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventSuccess,
                      spanName:
                        MPPTracingConstants.getRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanSuccess,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
          const imagesAlreadyLoaded = await firstValueFrom(
            this.store.select(selectRecipeImagesLoaded()),
          );
          if (!imagesAlreadyLoaded) {
            const ids = action.recipes.map(recipe => recipe.recipeId);
            await this.recipeImageService.getRecipeImageUris(ids);
          }
        }),
      ),
    { dispatch: false },
  );

  getRecipesFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.getRecipesFailure),
        tap(async action => {
          this.store.dispatch(
            MPP_ACTIONS.getRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.getRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.getRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventFailure,
                      spanName:
                        MPPTracingConstants.getRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanFailure,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  saveRecieImageUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.getRecipeProfileImagesFromS3Success),
        tap(async action => {
          await this.recipeImageService.saveDownImages(
            action.getImagesResponse,
          );
        }),
      ),
    { dispatch: false },
  );

  deleteRecipeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.deleteRecipesSuccess),
        tap(async action => {
          this.store.dispatch(
            MPP_ACTIONS.deleteRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.deleteRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.deleteRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventSuccess,
                      spanName:
                        MPPTracingConstants.deleteRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanSuccess,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  deleteRecipeFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.deleteRecipesFailure),
        tap(action => {
          this.store.dispatch(
            MPP_ACTIONS.deleteRecipes({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.deleteRecipes,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.deleteRecipesEvent +
                        ' ' +
                        MPPTracingConstants.eventFailure,
                      spanName:
                        MPPTracingConstants.deleteRecipesSpan +
                        ' ' +
                        MPPTracingConstants.spanFailure,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  getMyChangesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.getMyChangesSuccess),
        tap(async action => {
          this.store.dispatch(
            MPP_ACTIONS.getMyChanges({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.getMyChanges,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.getMyChangesEvent +
                        ' ' +
                        MPPTracingConstants.eventSuccess,
                      spanName:
                        MPPTracingConstants.getMyChangesEvent +
                        ' ' +
                        MPPTracingConstants.spanSuccess,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  getMyChangesFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RECIPE_ACTIONS.getMyChangesFailure),
        tap(async action => {
          this.store.dispatch(
            MPP_ACTIONS.getMyChanges({
              tracking: {
                tracing: {
                  data: {
                    traceContext: MPPTracingConstants.getMyChanges,
                    isEndOfTrace: true,
                    isStartOfTrace: false,
                    attributes: {
                      event:
                        MPPTracingConstants.getMyChangesEvent +
                        ' ' +
                        MPPTracingConstants.eventFailure,
                      spanName:
                        MPPTracingConstants.getMyChangesEvent +
                        ' ' +
                        MPPTracingConstants.spanFailure,
                    },
                  },
                  transformFunc: baseMPPTransform,
                },
              },
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  displayToastMessage(
    type: MessageTypeEnum,
    message: string,
    sticky = false,
  ): void {
    this.messageStoreService.upsertMessage({
      type,
      display: message,
      toast: true,
      sticky,
    } as Message);
  }

  constructor(
    private actions$: Actions,
    private translateService: TranslateService,
    private messageStoreService: MessageStoreService,
    private recipeImageService: RecipeImageService,
    public store: Store,
    private mppService: MppService,
  ) {}
}
